import dayjs from 'dayjs';
import { ReactNode, useCallback, useId, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useIssuerDividendEventInfiniteListQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';

interface DividendEventsSelectPaginateBaseProps {
  disabled?: boolean;
  disableWhileLoading?: boolean;
  entityId?: number;
  entityIds?: number[];
  emptyMessage?: string;
  error?: string;
  label?: ReactNode;
}

interface SingleDividendEventsSelectPaginateProps extends DividendEventsSelectPaginateBaseProps {
  isMulti?: false;
  onChange: (option: SelectPaginateOption<number> | null) => void;
  value?: SelectPaginateOption<number> | null;
}

interface MultiDividendEventsSelectPaginateProps extends DividendEventsSelectPaginateBaseProps {
  isMulti: true;
  onChange: (options: SelectPaginateOption<number>[]) => void;
  value?: SelectPaginateOption<number>[];
}

type DividendEventsSelectPaginateProps =
  | SingleDividendEventsSelectPaginateProps
  | MultiDividendEventsSelectPaginateProps;

export const DividendEventsSelectPaginate = ({
  disabled = false,
  disableWhileLoading = false,
  entityId,
  entityIds,
  emptyMessage,
  error,
  label,
  onChange,
  value,
  isMulti = false,
}: DividendEventsSelectPaginateProps) => {
  const getRegisterTypeString = useRegisterTypeStrings();
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const normalizedEntityIds = useMemo(
    () =>
      Array.from(
        new Set(
          (entityIds || (typeof entityId === 'number' ? [entityId] : [])).filter(
            (entityId): entityId is number =>
              typeof entityId === 'number' && !Number.isNaN(entityId),
          ),
        ),
      ).sort((a, b) => a - b),
    [entityId, entityIds],
  );
  const entityIdsCacheKey = normalizedEntityIds.join(',');

  const { data, fetchNextPage, isFetching, isFetchingNextPage, isLoading } =
    useIssuerDividendEventInfiniteListQuery(
      {
        page: 1,
        page_size: 15,
        ...(normalizedEntityIds.length ? { entity_ids: normalizedEntityIds } : {}),
      },
      { cacheKey: useId() },
    );
  const isDependentLoading = isLoading || (isFetching && !isFetchingNextPage);
  const isLoadingOptions = isDependentLoading || isFetchingNextPage;
  const showEmptyMessage =
    !!emptyMessage &&
    normalizedEntityIds.length > 0 &&
    !isDependentLoading &&
    !isFetchingNextPage &&
    (data?.pages?.[0]?.total || 0) === 0;

  const handleFetchNextDividends = async (
    page: number,
  ): Promise<SelectPaginateOption<number>[]> => {
    const dividendsData = await fetchNextPage({ pageParam: page });

    const dividends = dividendsData?.data?.pages?.at(-1)?.data || [];
    return dividends.map(({ id, register_class, date, name }) => ({
      label: (
        <div className="flex justify-between pr-2 items-center">
          <span className="flex gap-1 items-center">
            <span>{name}</span>
            <span>{register_class.class_name}</span>
          </span>
          <span className="text-xs text-gray-500">{dayjs(date).format('YYYY-MM-DD, HH:mm')}</span>
        </div>
      ),
      value: id,
    }));
  };

  const handleChange = useCallback(
    (option: SelectPaginateOption<number> | SelectPaginateOption<number>[] | null) => {
      if (isMulti) {
        (onChange as MultiDividendEventsSelectPaginateProps['onChange'])(
          (option || []) as SelectPaginateOption<number>[],
        );
        return;
      }
      (onChange as SingleDividendEventsSelectPaginateProps['onChange'])(
        option as SelectPaginateOption<number> | null,
      );
    },
    [isMulti, onChange],
  );

  return (
    <>
      <SelectPaginate<number, boolean>
        value={value}
        cacheUniqs={[registerId ?? null, entityIdsCacheKey || null]}
        label={label ?? `${getRegisterTypeString('dividend')} event${isMulti ? 's' : ''}`}
        error={error}
        onAsync={handleFetchNextDividends}
        onChange={handleChange}
        isMulti={isMulti}
        isClearable={true}
        showSelectedOptionsInMenu={isMulti}
        isLoading={isLoadingOptions}
        disabled={disabled || (disableWhileLoading && isDependentLoading)}
      />
      {showEmptyMessage && <div className="!mt-2 text-sm text-[#DC2626]">{emptyMessage}</div>}
    </>
  );
};
